import { useCallback, useEffect, useState } from "react";
import { useDebounce } from "react-use";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "../ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { cn } from "@/lib/utils";

export type Option = { value: string; label: string };

export type AsyncSelectProps = {
	value?: string;
	placeholder?: string;
	debounce?: number;
	disabled?: boolean;
	className?: string;

	/**
	 * Initial set of select options before doing any remote query.
	 */
	initialOptions?: Option[];

	/**
	 * Callback when the query changes.
	 *
	 * @param query The query string.
	 * @returns A promise that resolves to an array of options.
	 */
	onQueryChange: (query: string) => Promise<Option[]>;

	/**
	 * Callback function to handle the change of the selected option.
	 *
	 * @param value The value of the selected option.
	 */
	onChange?: (v: string | undefined) => unknown;
};

const DEFAULT_PLACEHOLDER = "Search...";
const DEFAULT_DEBOUNCE = 300;

export function AsyncSelect({
	value,
	onChange,
	onQueryChange,
	placeholder = DEFAULT_PLACEHOLDER,
	debounce = DEFAULT_DEBOUNCE,
	disabled = false,
	initialOptions = [],
	className,
}: AsyncSelectProps) {
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState("");
	const [items, setItems] = useState<Option[]>(initialOptions);
	const [loading, setLoading] = useState(false);
	const [currentOption, setCurrentOption] = useState<Option | undefined>(
		initialOptions.find((item) => item.value === value),
	);

	useDebounce(
		async () => {
			if (disabled) {
				return;
			}

			if (!search.trim()) {
				setItems([]);
				return;
			}

			setLoading(true);

			try {
				const data = await onQueryChange(search);
				setItems(data);
			} catch {
				// Silent failure - keep previous items
			} finally {
				setLoading(false);
			}
		},
		debounce,
		[search, disabled],
	);

	useEffect(() => {
		if (disabled && open) {
			setOpen(false);
		}
	}, [disabled, open]);

	const handleItemClick = useCallback(
		(selectedValue: string) => {
			if (selectedValue !== value) {
				onChange?.(selectedValue);

				// store the selected item to always have it available
				const selectedItem = items.find((item) => item.value === selectedValue);
				setCurrentOption(selectedItem);
			}

			setOpen(false);
		},
		[items, value, onChange],
	);

	// if the initial options change we set the items with it's value
	useEffect(() => {
		setItems(initialOptions);
	}, [initialOptions]);

	return (
		<Popover
			open={disabled ? false : open}
			onOpenChange={disabled ? undefined : setOpen}
		>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					role="combobox"
					aria-expanded={open}
					aria-disabled={disabled}
					className={cn("w-full justify-between", className)}
					disabled={disabled}
				>
					{currentOption?.label ?? value ?? placeholder}
					<ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
				</Button>
			</PopoverTrigger>

			<PopoverContent className="w-[--radix-popover-trigger-width] p-0">
				<Command shouldFilter={false}>
					<CommandInput
						placeholder={placeholder}
						value={search}
						onValueChange={setSearch}
						disabled={disabled}
					/>
					<CommandList>
						{loading && (
							<div className="p-2 text-sm text-muted-foreground">Loading…</div>
						)}
						<CommandEmpty>
							{search ? "No results." : "Type to search."}
						</CommandEmpty>

						<CommandGroup>
							{items.map((item) => (
								<CommandItem
									key={item.value}
									value={item.value}
									onSelect={handleItemClick}
								>
									{item.label}
									<Check
										className={cn(
											"ml-auto h-4 w-4",
											value === item.value ? "opacity-100" : "opacity-0",
										)}
									/>
								</CommandItem>
							))}
						</CommandGroup>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	);
}
